import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Building, Plus, Search, X, Edit, Trash2, Save, Mail, Phone, Globe, FileText, MapPin, List, Grid, ChevronLeft } from 'lucide-react';
import { api } from '../config/api';
import { catalogService } from '../services/catalogService';
import { parseErrorMessage } from '../utils/helpers';

const COMPANY_ROLE_OPTIONS = [
  { value: 'CLIENT', label: 'Cliente' },
  { value: 'SUPPLIER', label: 'Agente' },
  { value: 'FACTORY', label: 'Fábrica' },
];

const PORT_TYPE_OPTIONS = [
  { value: 'FACTORY_PORT', label: 'Puerto Fábrica' },
  { value: 'DESTINATION_PORT', label: 'Puerto Destino' },
];

const COMPANY_FORM_TABS = [
  { id: 'general', label: 'General' },
  { id: 'contactos', label: 'Contactos' },
  { id: 'financiero', label: 'Financiero' },
  { id: 'envio', label: 'Información de envío' },
  { id: 'otra', label: 'Otra información' },
];

const CompanyManager = ({ 
  companies = [], 
  contacts = [],
  onQuickEditContact = () => {},
  onAddCompany = () => {},
  onUpdateCompany = () => {},
  onDeleteCompany = () => {}
}) => {
  const [showForm, setShowForm] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('list');
  const [ports, setPorts] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [creditDaysOptions, setCreditDaysOptions] = useState([]);
  const [balancePayments, setBalancePayments] = useState([]);
  const [formActiveTab, setFormActiveTab] = useState('general');
  
  const [formData, setFormData] = useState({
    companyRole: 'CLIENT',
    name: '',
    razonSocial: '',
    cuit: '',
    comprador: '',
    descripcion: '',
    address: '',
    city: '',
    state: '',
    country: 'Argentina',
    phone: '',
    email: '',
    website: '',
    notes: '',
    paymentMethodId: '',
    creditDaysId: '',
    balancePaymentId: '',
    bankAccounts: [],
    companyPorts: [],
  });

  useEffect(() => {
    const loadPorts = async () => {
      try {
        const portsData = await api.get('/pricing/ports/');
        setPorts(Array.isArray(portsData) ? portsData : []);
      } catch (error) {
        console.error('Error cargando puertos:', error);
        setPorts([]);
      }
    };
    const loadCatalogs = async () => {
      try {
        const [pmData, cdData, bpData] = await Promise.all([
          catalogService.paymentMethods.getAll({ active_only: true }),
          catalogService.creditDays.getAll({ active_only: true }),
          catalogService.balancePayments.getAll({ active_only: true }),
        ]);
        setPaymentMethods(Array.isArray(pmData) ? pmData : []);
        setCreditDaysOptions(Array.isArray(cdData) ? cdData : []);
        setBalancePayments(Array.isArray(bpData) ? bpData : []);
      } catch (error) {
        console.error('Error cargando catálogos financieros:', error);
      }
    };
    loadPorts();
    loadCatalogs();
  }, []);

  const resetForm = () => {
    setFormData({
      companyRole: 'CLIENT',
      name: '',
      razonSocial: '',
      cuit: '',
      comprador: '',
      descripcion: '',
      address: '',
      city: '',
      state: '',
      country: 'Argentina',
      phone: '',
      email: '',
      website: '',
      notes: '',
      paymentMethodId: '',
      creditDaysId: '',
      balancePaymentId: '',
      bankAccounts: [],
      companyPorts: [],
    });
    setEditingCompany(null);
    setFormActiveTab('general');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCompany) {
        await onUpdateCompany(editingCompany.id, formData);
      } else {
        await onAddCompany(formData);
      }
      setShowForm(false);
      resetForm();
    } catch (error) {
      console.error('Error guardando empresa:', error);
      alert(`No se pudo guardar la empresa.\n${parseErrorMessage(error, 'Revisá los datos obligatorios e intentá de nuevo.')}`);
    }
  };

  const handleEdit = (company) => {
    setEditingCompany(company);
    setFormData({
      companyRole: company.companyRole || 'CLIENT',
      name: company.name || '',
      razonSocial: company.razonSocial || '',
      cuit: company.cuit || '',
      comprador: company.comprador || '',
      descripcion: company.descripcion || '',
      address: company.address || '',
      city: company.city || '',
      state: company.state || '',
      country: company.country || 'Argentina',
      phone: company.phone || '',
      email: company.email || '',
      website: company.website || '',
      notes: company.notes || '',
      paymentMethodId: company.paymentMethod ? String(company.paymentMethod) : '',
      creditDaysId: company.creditDays ? String(company.creditDays) : '',
      balancePaymentId: company.balancePayment ? String(company.balancePayment) : '',
      bankAccounts: Array.isArray(company.bankAccounts) ? company.bankAccounts : [],
      companyPorts: Array.isArray(company.companyPorts) ? company.companyPorts : [],
    });
    setFormActiveTab('general');
    setShowForm(true);
  };

  const addBankAccount = () => {
    setFormData((prev) => ({
      ...prev,
      bankAccounts: [
        ...prev.bankAccounts,
        {
          beneficiaryAccountName: '',
          beneficiaryBankAddress: '',
          routingAba: '',
          swiftBic: '',
          accountNumber: '',
          currency: '',
        },
      ],
    }));
  };

  const updateBankAccount = (idx, field, value) => {
    setFormData((prev) => ({
      ...prev,
      bankAccounts: prev.bankAccounts.map((acc, i) => (i === idx ? { ...acc, [field]: value } : acc)),
    }));
  };

  const removeBankAccount = (idx) => {
    setFormData((prev) => ({
      ...prev,
      bankAccounts: prev.bankAccounts.filter((_, i) => i !== idx),
    }));
  };

  const addCompanyPort = () => {
    setFormData((prev) => ({
      ...prev,
      companyPorts: [...prev.companyPorts, { portId: '', portType: 'DESTINATION_PORT' }],
    }));
  };

  const updateCompanyPort = (idx, field, value) => {
    setFormData((prev) => ({
      ...prev,
      companyPorts: prev.companyPorts.map((port, i) => (i === idx ? { ...port, [field]: value } : port)),
    }));
  };

  const removeCompanyPort = (idx) => {
    setFormData((prev) => ({
      ...prev,
      companyPorts: prev.companyPorts.filter((_, i) => i !== idx),
    }));
  };

  const handleDelete = async (companyId) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar esta empresa?')) {
      try {
        await onDeleteCompany(companyId);
      } catch (error) {
        console.error('Error eliminando empresa:', error);
        alert(`No se pudo eliminar la empresa.\n${parseErrorMessage(error)}`);
      }
    }
  };

  const filteredCompanies = useMemo(() => {
    return companies.filter(company => {
      const searchLower = searchTerm.toLowerCase();
      return (
        company.name?.toLowerCase().includes(searchLower) ||
        company.razonSocial?.toLowerCase().includes(searchLower) ||
        company.cuit?.toLowerCase().includes(searchLower) ||
        company.city?.toLowerCase().includes(searchLower)
      );
    });
  }, [companies, searchTerm]);

  const linkedContacts = useMemo(() => {
    if (!editingCompany?.id) return [];
    const companyId = String(editingCompany.id);
    return contacts.filter((contact) => {
      if (contact.companyId && String(contact.companyId) === companyId) return true;
      if (Array.isArray(contact.companyIds)) {
        return contact.companyIds.map(String).includes(companyId);
      }
      return false;
    });
  }, [contacts, editingCompany]);

  return (
    <div className="space-y-4">
      {showForm ? (
        <>
          <div className="flex items-center gap-3 mb-2">
            <button
              onClick={() => { setShowForm(false); resetForm(); }}
              className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
            >
              <ChevronLeft className="w-4 h-4" />
              Volver a Empresas
            </button>
            <span className="text-sm text-gray-400">/</span>
            <span className="text-sm font-medium text-gray-700">
              {editingCompany ? 'Editar Empresa' : 'Nueva Empresa'}
            </span>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              {editingCompany ? 'Editar Empresa' : 'Nueva Empresa'}
            </h3>

            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2 border-b border-gray-200 pb-2 flex flex-wrap gap-2">
                {COMPANY_FORM_TABS.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setFormActiveTab(tab.id)}
                    className={`px-3 py-1.5 text-sm rounded-t-lg border-b-2 transition-colors ${
                      formActiveTab === tab.id
                        ? 'text-blue-700 border-blue-700 font-semibold'
                        : 'text-gray-600 border-transparent hover:text-gray-800 hover:border-gray-300'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {formActiveTab === 'general' && (
                <>
                  <div className="md:col-span-2">
                    <div className="px-4 py-2 bg-blue-700 text-white text-sm font-semibold rounded-lg">
                      Datos generales de empresa
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Rol de empresa *</label>
                    <select
                      value={formData.companyRole}
                      onChange={(e) => setFormData({...formData, companyRole: e.target.value})}
                      className="w-full text-sm px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                      required
                    >
                      {COMPANY_ROLE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Nombre *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full text-sm px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Comprador</label>
                    <input
                      type="text"
                      value={formData.comprador}
                      onChange={(e) => setFormData({...formData, comprador: e.target.value})}
                      className="w-full text-sm px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Razón Social</label>
                    <input
                      type="text"
                      value={formData.razonSocial}
                      onChange={(e) => setFormData({...formData, razonSocial: e.target.value})}
                      className="w-full text-sm px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">CUIT</label>
                    <input
                      type="text"
                      value={formData.cuit}
                      onChange={(e) => setFormData({...formData, cuit: e.target.value})}
                      className="w-full text-sm px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                      placeholder="XX-XXXXXXXX-X"
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Dirección</label>
                    <input
                      type="text"
                      value={formData.address}
                      onChange={(e) => setFormData({...formData, address: e.target.value})}
                      className="w-full text-sm px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Ciudad</label>
                    <input
                      type="text"
                      value={formData.city}
                      onChange={(e) => setFormData({...formData, city: e.target.value})}
                      className="w-full text-sm px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Provincia</label>
                    <input
                      type="text"
                      value={formData.state}
                      onChange={(e) => setFormData({...formData, state: e.target.value})}
                      className="w-full text-sm px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">País</label>
                    <input
                      type="text"
                      value={formData.country}
                      onChange={(e) => setFormData({...formData, country: e.target.value})}
                      className="w-full text-sm px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Teléfono</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="w-full text-sm px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full text-sm px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Sitio Web</label>
                    <input
                      type="url"
                      value={formData.website}
                      onChange={(e) => setFormData({...formData, website: e.target.value})}
                      className="w-full text-sm px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                      placeholder="https://..."
                    />
                  </div>
                </>
              )}

              {formActiveTab === 'contactos' && (
                <>
                  <div className="md:col-span-2">
                    <div className="px-4 py-2 bg-blue-700 text-white text-sm font-semibold rounded-lg">
                      Contactos vinculados
                    </div>
                  </div>
                  {!editingCompany ? (
                    <div className="md:col-span-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-xl px-4 py-4">
                      Guarda primero la empresa para ver y vincular contactos persona desde el módulo <strong>Contactos</strong>.
                    </div>
                  ) : linkedContacts.length === 0 ? (
                    <div className="md:col-span-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-xl px-4 py-4">
                      Esta empresa no tiene contactos vinculados todavía.
                    </div>
                  ) : (
                    <div className="md:col-span-2 bg-white border border-gray-200 rounded-xl overflow-hidden">
                      <div className="px-4 py-3 border-b border-gray-200 text-sm font-semibold text-gray-700">
                        Contactos vinculados ({linkedContacts.length})
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Nombre</th>
                              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Email</th>
                              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Teléfono</th>
                              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Tipo</th>
                              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Acción</th>
                            </tr>
                          </thead>
                          <tbody>
                            {linkedContacts.map((contact) => (
                              <tr key={contact.id} className="border-t border-gray-100">
                                <td className="px-4 py-2 text-sm text-gray-800">{contact.name || '-'}</td>
                                <td className="px-4 py-2 text-sm text-gray-600">{contact.email || '-'}</td>
                                <td className="px-4 py-2 text-sm text-gray-600">{contact.phone || '-'}</td>
                                <td className="px-4 py-2 text-sm text-gray-600">{contact.contactType || '-'}</td>
                                <td className="px-4 py-2 text-sm">
                                  <button
                                    type="button"
                                    onClick={() => onQuickEditContact(contact)}
                                    className="text-blue-600 hover:text-blue-800 font-medium"
                                  >
                                    Editar en Contactos
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              )}

              {formActiveTab === 'financiero' && (
                <>
                  <div className="md:col-span-2">
                    <div className="px-4 py-2 bg-blue-700 text-white text-sm font-semibold rounded-lg">
                      Financiero
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Forma de pago</label>
                    <select
                      value={formData.paymentMethodId}
                      onChange={(e) => setFormData({...formData, paymentMethodId: e.target.value})}
                      className="w-full text-sm px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                    >
                      <option value="">Seleccionar</option>
                      {paymentMethods.map((pm) => (
                        <option key={pm.id} value={pm.id}>{pm.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Días de crédito</label>
                    <select
                      value={formData.creditDaysId}
                      onChange={(e) => setFormData({...formData, creditDaysId: e.target.value})}
                      className="w-full text-sm px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                    >
                      <option value="">Seleccionar</option>
                      {creditDaysOptions.map((cd) => (
                        <option key={cd.id} value={cd.id}>{cd.days} días</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Balance Payment</label>
                    <select
                      value={formData.balancePaymentId}
                      onChange={(e) => setFormData({...formData, balancePaymentId: e.target.value})}
                      className="w-full text-sm px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                    >
                      <option value="">Seleccionar</option>
                      {balancePayments.map((bp) => (
                        <option key={bp.id} value={bp.id}>{bp.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="md:col-span-2 border-t border-gray-200 pt-4 mt-2">
                    <div className="flex items-center justify-between mb-3">
                      <label className="block text-sm font-semibold text-gray-800">Cuentas bancarias</label>
                      <button
                        type="button"
                        onClick={addBankAccount}
                        className="px-3 py-1.5 text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-100"
                      >
                        + Agregar cuenta
                      </button>
                    </div>
                    <div className="space-y-3">
                      {formData.bankAccounts.map((account, idx) => (
                        <div key={`acc-${idx}`} className="grid grid-cols-1 md:grid-cols-3 gap-3 bg-white border border-gray-200 rounded-xl p-3">
                          <input
                            type="text"
                            placeholder="Beneficiary Account Name"
                            value={account.beneficiaryAccountName || ''}
                            onChange={(e) => updateBankAccount(idx, 'beneficiaryAccountName', e.target.value)}
                            className="text-sm px-3 py-2 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                          />
                          <input
                            type="text"
                            placeholder="Número de cuenta"
                            value={account.accountNumber || ''}
                            onChange={(e) => updateBankAccount(idx, 'accountNumber', e.target.value)}
                            className="text-sm px-3 py-2 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                          />
                          <input
                            type="text"
                            placeholder="Divisa"
                            value={account.currency || ''}
                            onChange={(e) => updateBankAccount(idx, 'currency', e.target.value)}
                            className="text-sm px-3 py-2 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                          />
                          <input
                            type="text"
                            placeholder="Beneficiary Bank Address"
                            value={account.beneficiaryBankAddress || ''}
                            onChange={(e) => updateBankAccount(idx, 'beneficiaryBankAddress', e.target.value)}
                            className="text-sm px-3 py-2 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 md:col-span-2"
                          />
                          <div className="flex gap-2">
                            <input
                              type="text"
                              placeholder="Routing/ABA"
                              value={account.routingAba || ''}
                              onChange={(e) => updateBankAccount(idx, 'routingAba', e.target.value)}
                              className="text-sm px-3 py-2 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 w-full"
                            />
                            <input
                              type="text"
                              placeholder="SWIFT/BIC"
                              value={account.swiftBic || ''}
                              onChange={(e) => updateBankAccount(idx, 'swiftBic', e.target.value)}
                              className="text-sm px-3 py-2 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 w-full"
                            />
                            <button
                              type="button"
                              onClick={() => removeBankAccount(idx)}
                              className="px-3 py-2 text-red-600 border border-red-200 rounded-lg hover:bg-red-50"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {formActiveTab === 'envio' && (
                <>
                  <div className="md:col-span-2">
                    <div className="px-4 py-2 bg-blue-700 text-white text-sm font-semibold rounded-lg">
                      Información de envío
                    </div>
                  </div>
                  <div className="md:col-span-2 border-t border-gray-200 pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <label className="block text-sm font-semibold text-gray-800">Puertos</label>
                      <button
                        type="button"
                        onClick={addCompanyPort}
                        className="px-3 py-1.5 text-xs bg-green-50 text-green-700 border border-green-200 rounded-lg hover:bg-green-100"
                      >
                        + Agregar puerto
                      </button>
                    </div>
                    <div className="space-y-3">
                      {formData.companyPorts.map((companyPort, idx) => (
                        <div key={`port-${idx}`} className="grid grid-cols-1 md:grid-cols-3 gap-3 bg-white border border-gray-200 rounded-xl p-3">
                          <select
                            value={companyPort.portId || ''}
                            onChange={(e) => updateCompanyPort(idx, 'portId', e.target.value)}
                            className="text-sm px-3 py-2 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                          >
                            <option value="">Seleccionar puerto</option>
                            {ports.map((port) => (
                              <option key={port.id} value={port.id}>{port.name}</option>
                            ))}
                          </select>
                          <select
                            value={companyPort.portType || 'DESTINATION_PORT'}
                            onChange={(e) => updateCompanyPort(idx, 'portType', e.target.value)}
                            className="text-sm px-3 py-2 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                          >
                            {PORT_TYPE_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </select>
                          <button
                            type="button"
                            onClick={() => removeCompanyPort(idx)}
                            className="px-3 py-2 text-red-600 border border-red-200 rounded-lg hover:bg-red-50"
                          >
                            Eliminar
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {formActiveTab === 'otra' && (
                <>
                  <div className="md:col-span-2">
                    <div className="px-4 py-2 bg-blue-700 text-white text-sm font-semibold rounded-lg">
                      Otra información
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Descripción</label>
                    <textarea
                      value={formData.descripcion}
                      onChange={(e) => setFormData({...formData, descripcion: e.target.value})}
                      className="w-full text-sm px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 resize-none"
                      rows={2}
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Notas</label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({...formData, notes: e.target.value})}
                      className="w-full text-sm px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 resize-none"
                      rows={3}
                    />
                  </div>
                </>
              )}
                
              <div className="md:col-span-2 flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    resetForm();
                  }}
                  className="px-6 py-2 text-gray-600 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  {editingCompany ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </>
      ) : (
        <>
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-600 rounded-xl">
                  <Building className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-800">Gestión de Empresas</h1>
                  <p className="text-gray-500">Administra las empresas de tus clientes</p>
                </div>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    resetForm();
                    setShowForm(true);
                  }}
                  className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold shadow-sm transition-colors flex items-center gap-2"
                >
                  <Plus className="w-5 h-5" />
                  Nueva Empresa
                </button>
              </div>
            </div>

            <div>
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Buscar por nombre, razón social, CUIT o ciudad..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-800">
                Empresas ({filteredCompanies.length})
              </h2>
            </div>
            
            {filteredCompanies.length === 0 ? (
              <div className="text-center py-12">
                <Building className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-500 mb-2">No hay empresas</h3>
                <p className="text-gray-400">Agrega tu primera empresa para comenzar</p>
              </div>
            ) : viewMode === 'list' ? (
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Empresa</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Rol</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">CUIT</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Ciudad</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Email</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Teléfono</th>
                        <th className="px-6 py-4 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredCompanies.map((company, index) => (
                        <motion.tr
                          key={company.id}
                          className="hover:bg-gray-50 transition-colors"
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.05 }}
                        >
                          <td className="px-6 py-4">
                            <div className="text-sm font-semibold text-gray-900">{company.name}</div>
                            {company.razonSocial && (
                              <div className="text-xs text-gray-500">{company.razonSocial}</div>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">{company.companyRole || '-'}</td>
                          <td className="px-6 py-4 text-sm text-gray-600">{company.cuit || '-'}</td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {company.city ? `${company.city}${company.state ? `, ${company.state}` : ''}` : '-'}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">{company.email || '-'}</td>
                          <td className="px-6 py-4 text-sm text-gray-600">{company.phone || '-'}</td>
                          <td className="px-6 py-4 text-center">
                            <div className="flex justify-center gap-2">
                              <button
                                onClick={() => handleEdit(company)}
                                className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                                title="Editar"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(company.id)}
                                className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                                title="Eliminar"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <AnimatePresence>
                  {filteredCompanies.map((company, index) => (
                    <motion.div
                      key={company.id}
                      className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.4, delay: index * 0.05 }}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-blue-500 rounded-lg">
                            <Building className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <h3 className="text-lg font-bold text-gray-800">{company.name}</h3>
                            {company.razonSocial && (
                              <p className="text-sm text-gray-600">{company.razonSocial}</p>
                            )}
                            <p className="text-xs text-blue-700 font-medium">{company.companyRole || 'CLIENT'}</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEdit(company)}
                            className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                            title="Editar"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(company.id)}
                            className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                            title="Eliminar"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      
                      <div className="space-y-2 text-sm text-gray-600">
                        {company.cuit && (
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4" />
                            <span>CUIT: {company.cuit}</span>
                          </div>
                        )}
                        {company.city && (
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4" />
                            <span>{company.city}{company.state ? `, ${company.state}` : ''}</span>
                          </div>
                        )}
                        {company.phone && (
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4" />
                            <span>{company.phone}</span>
                          </div>
                        )}
                        {company.email && (
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4" />
                            <span className="truncate">{company.email}</span>
                          </div>
                        )}
                        {company.website && (
                          <div className="flex items-center gap-2">
                            <Globe className="w-4 h-4" />
                            <a 
                              href={company.website.startsWith('http') ? company.website : `https://${company.website}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline truncate"
                            >
                              {company.website}
                            </a>
                          </div>
                        )}
                        <div className="text-xs text-gray-500 pt-1">
                          Cuentas bancarias: {(company.bankAccounts || []).length} | Puertos: {(company.companyPorts || []).length}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default CompanyManager;
